import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { theme, countTag, emptyFrame, type as text } from "../theme";
import { api } from "../api/client";
import { StatusTag } from "./Drawing";
import { DetailSection } from "./RecordView";
import { fmtDate } from "../lib/format";
import { COVERAGE_TEXT, COVERAGE_TONE, type Coverage } from "./EvidenceQualityReview";

// "What satisfies this requirement" on one SOA row: the documents, adopted policies and register
// records on file against the control, the risks and corrective actions linked to it, and on a
// gap the register or policy template that would close it. Rules in backend
// lib/requirementSatisfaction.ts; loaded only when the row is opened.

// advice: the document's quality review on filing against this requirement (backend lib/evidenceQuality.ts).
// Advice only: it never makes or clears the gap below.
interface Advice { coverage: Coverage | null; reason: string | null; concerns: string[] }

interface Satisfaction {
  // outOfDate: the document's validity period has run out (backend lib/evidenceValidity.ts); it stays listed but is not current.
  satisfiedBy: Array<{ kind: "document" | "policy" | "register"; id: string; label: string; detail: string; href: string; at: string; outOfDate?: { typeLabel: string; evidenceDate: string; lapsedOn: string }; validUntil?: string; advice?: Advice }>;
  risks: Array<{ id: string; reference: string; title: string; status: string; residualScore: number | null; href: string }>;
  actions: Array<{ id: string; reference: string; title: string; status: string; source: string; href: string; open: boolean }>;
  gap: boolean;
  closers: Array<{ kind: "template" | "register" | "evidence"; label: string; reason: string; href: string | null }>;
}

const KIND_LABEL: Record<Satisfaction["satisfiedBy"][number]["kind"], string> = {
  document: "Document",
  policy: "Policy",
  register: "Register",
};

const ACTION_STATUS: Record<string, string> = {
  proposed: "Proposed", approved: "Approved", in_progress: "In progress", blocked: "Blocked",
  completed: "Completed, awaiting verification", verified: "Verified",
};

const linkStyle = { ...text.body, color: theme.color.ref, textDecoration: "none", fontWeight: 600 } as const;
const rowStyle = { display: "flex", alignItems: "baseline", gap: 8, padding: "6px 0", borderTop: `1px solid ${theme.color.border}`, flexWrap: "wrap" } as const;
const kindTag = { ...text.meta, fontWeight: 700, padding: "0 6px", borderRadius: 2, border: `1px solid ${theme.color.line}`, color: theme.color.muted, whiteSpace: "nowrap" } as const;

export function RequirementSatisfaction({ controlId }: { controlId: string }) {
  const [data, setData] = useState<Satisfaction | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    api.get<Satisfaction>(`/controls/${encodeURIComponent(controlId)}/satisfaction`)
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setError("Could not load what evidences this requirement."); });
    return () => { live = false; };
  }, [controlId]);

  const current = data ? data.satisfiedBy.filter((s) => !s.outOfDate) : [];
  const aside = data
    ? <StatusTag tone={data.gap ? (current.length ? "partial" : "gap") : "met"}>{data.gap ? (current.length ? "Partly evidenced" : data.satisfiedBy.length ? "Evidence out of date" : "Not evidenced") : "Evidenced"}</StatusTag>
    : undefined;

  return (
    <DetailSection title="What satisfies this requirement" aside={aside}>
      {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
      {!data && !error && <div style={text.hint}>Loading…</div>}
      {data && (
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <div style={{ ...text.label, display: "flex", alignItems: "center", gap: 6 }}>
              On file <span style={countTag}>{data.satisfiedBy.length}</span>
            </div>
            {data.satisfiedBy.length === 0 ? (
              <div style={{ ...emptyFrame, padding: "12px 14px", textAlign: "left", ...text.hint }}>
                No document, adopted policy or register record is on file against this requirement yet.
              </div>
            ) : data.satisfiedBy.map((s) => (
              <div key={s.id} style={rowStyle}>
                <span style={kindTag}>{KIND_LABEL[s.kind]}</span>
                <Link to={s.href} style={linkStyle}>{s.label}</Link>
                <span style={text.hint}>{s.detail}{s.at ? ` · ${fmtDate(s.at)}` : ""}</span>
                {s.outOfDate && <StatusTag tone="gap">Out of date</StatusTag>}
                {s.advice && (
                  <span style={{ ...text.hint, display: "inline-flex", gap: 6, alignItems: "baseline", flexWrap: "wrap", flexBasis: "100%" }}>
                    <span>Review (advice):</span>
                    {s.advice.coverage && <StatusTag tone={COVERAGE_TONE[s.advice.coverage]}>{COVERAGE_TEXT[s.advice.coverage]}</StatusTag>}
                    {s.advice.reason && <span>{s.advice.reason}</span>}
                    {s.advice.concerns.length > 0 && <span>Check: {s.advice.concerns.join("; ").toLowerCase()}.</span>}
                  </span>
                )}
              </div>
            ))}
          </div>

          {(data.risks.length > 0 || data.actions.length > 0) && (
            <div>
              <div style={{ ...text.label, display: "flex", alignItems: "center", gap: 6 }}>
                Linked <span style={countTag}>{data.risks.length + data.actions.length}</span>
              </div>
              {data.risks.map((r) => (
                <div key={r.id} style={rowStyle}>
                  <span style={kindTag}>Risk</span>
                  <Link to={r.href} style={linkStyle}><span style={text.ref}>{r.reference}</span> {r.title}</Link>
                  <span style={text.hint}>{r.status}{r.residualScore != null ? ` · residual ${r.residualScore}` : ""}</span>
                </div>
              ))}
              {data.actions.map((a) => (
                <div key={a.id} style={rowStyle}>
                  <span style={kindTag}>Action</span>
                  <Link to={a.href} style={linkStyle}><span style={text.ref}>{a.reference}</span> {a.title}</Link>
                  <span style={text.hint}>{ACTION_STATUS[a.status] ?? a.status}</span>
                </div>
              ))}
            </div>
          )}

          {data.gap && data.closers.length > 0 && (
            <div>
              <div style={text.label}>What would close the gap</div>
              {data.closers.map((c, i) => (
                <div key={`${c.kind}-${c.href ?? i}`} style={rowStyle}>
                  <span style={kindTag}>{c.kind === "template" ? "Policy template" : c.kind === "register" ? "Register" : "Evidence"}</span>
                  {c.href ? <Link to={c.href} style={linkStyle}>{c.label}</Link> : <span style={{ ...text.body, fontWeight: 600 }}>{c.label}</span>}
                  <span style={text.hint}>{c.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </DetailSection>
  );
}
